import { useCallback, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import axios from "../../../api/axios";
import {
  BottomSheet,
  rowButtonStyle,
  PickerCheckIcon,
  EmptyRow,
  LoadingRow,
} from "../hours/hourFormPickers";

/**
 * Static option picker — bottom sheet for fixed option lists (event type, severity, status, …).
 */
export const StaticOptionPickerSheet = ({
  open,
  onClose,
  title,
  options = [],
  selectedValue,
  onSelect,
  searchPlaceholder,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const q = query.trim().toLowerCase();
  const rows = q
    ? options.filter((o) => String(o.title || "").toLowerCase().includes(q))
    : options;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={title}
      searchValue={query}
      onSearch={setQuery}
      searchPlaceholder={searchPlaceholder || t("Søk…")}
    >
      {rows.length === 0 ? (
        <EmptyRow text={t("Ingen treff")} />
      ) : (
        rows.map((opt) => {
          const isSel = String(opt.value) === String(selectedValue);
          return (
            <button
              key={opt.value}
              type="button"
              style={rowButtonStyle}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(opt);
                onClose();
              }}
            >
              <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: "#0F172A" }}>{opt.title}</span>
              {isSel && <PickerCheckIcon />}
            </button>
          );
        })
      )}
    </BottomSheet>
  );
};

/** Department picker — searchable bottom sheet. */
export const DepartmentPickerSheet = ({ open, onClose, selectedId, onSelect }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await axios.get("/departments/", {
        params: {
          query: q || undefined,
          page: 1,
          per_page: 50,
          sortBy: "displayTitle",
          sort: "asc",
          fields: "id,displayTitle",
        },
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(data.map((r) => ({ id: r.id, name: r.displayTitle ?? "", object: r })));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debounced = useRef(debounce((q) => fetchRows(q), 300)).current;
  useEffect(() => {
    if (!open) return undefined;
    debounced(query);
    return () => debounced.cancel();
  }, [query, open, debounced]);
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t("Velg avdeling")}
      searchValue={query}
      onSearch={setQuery}
      searchPlaceholder={t("Søk avdeling…")}
    >
      {loading ? (
        <LoadingRow text={t("Laster…")} />
      ) : rows.length === 0 ? (
        <EmptyRow text={t("Ingen avdelinger funnet")} />
      ) : (
        <>
          <button
            type="button"
            style={rowButtonStyle}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onSelect(null);
              onClose();
            }}
          >
            <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: "#94A3B8" }}>{t("Ingen valgt")}</span>
            {!selectedId && <PickerCheckIcon />}
          </button>
          {rows.map((row) => {
            const isSel = String(row.id) === String(selectedId);
            return (
              <button
                key={row.id}
                type="button"
                style={rowButtonStyle}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(row.object);
                  onClose();
                }}
              >
                <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: "#0F172A" }}>{row.name}</span>
                {isSel && <PickerCheckIcon />}
              </button>
            );
          })}
        </>
      )}
    </BottomSheet>
  );
};
